import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DollarSign, User, FileText, LogOut, Menu, Calculator } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { API_BASE } from '../../utils/constants';
import ExpenseCalculator from '../ui/ExpenseCalculator';

const Navbar = ({ showMobileMenu, setShowMobileMenu }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      logout();
      navigate("/");
      if (window.showNotification) {
        window.showNotification('success', 'Logged out successfully');
      }
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_BASE}/users/email/${user.email}`);
      const userData = await res.json();
      setProfileData(userData);
    } catch (err) {
      console.error("Failed to fetch profile", err);
    }
  };

  useEffect(() => {
    if (user && showDropdown) {
      fetchProfile();
    }
  }, [user, showDropdown]);

  return (
    <>
      <nav className="bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/dashboard" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-teal-500" />
              </div>
              <span className="text-xl font-bold">SplitWise</span>
            </Link>
            
            {user && (
              <>
                {/* Desktop Menu */}
                <div className="hidden md:flex items-center space-x-6">
                  <Link 
                    to="/dashboard" 
                    className="hover:text-teal-200 transition-colors font-medium px-2 py-1 rounded"
                  >
                    Dashboard
                  </Link>
                  <Link 
                    to="/create-group" 
                    className="hover:text-teal-200 transition-colors font-medium px-2 py-1 rounded"
                  >
                    Create Group
                  </Link>
                  <Link 
                    to="/add-expense" 
                    className="hover:text-teal-200 transition-colors font-medium px-2 py-1 rounded"
                  >
                    Add Expense
                  </Link>
                  
                  {/* Calculator Button */}
                  <button
                    onClick={() => setShowCalculator(true)}
                    className="hover:text-teal-200 transition-colors font-medium flex items-center space-x-2 px-2 py-1 rounded hover:bg-teal-600"
                    title="Expense Calculator"
                  >
                    <Calculator className="h-4 w-4" />
                    <span className="hidden lg:inline">Calculator</span>
                  </button>
                  
                  <div className="relative">
                    <button
                      onClick={() => setShowDropdown(!showDropdown)}
                      className="w-9 h-9 bg-teal-400 rounded-full flex items-center justify-center hover:bg-teal-300 transition-colors shadow-sm"
                    >
                      <User className="h-4 w-4" />
                    </button>
                    
                    {showDropdown && (
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-xl py-2 z-50 border border-gray-100">
                        <div className="px-4 py-3 border-b border-gray-100">
                          <p className="text-sm font-semibold text-gray-900">{profileData?.name || 'User'}</p>
                          <p className="text-sm text-gray-500 truncate">{profileData?.email || user.email}</p>
                        </div>
                        <Link
                          to="/transactions"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={() => setShowDropdown(false)}
                        >
                          <FileText className="h-4 w-4 mr-3 text-gray-500" />
                          My Transactions
                        </Link>
                        <button
                          onClick={() => {
                            setShowCalculator(true);
                            setShowDropdown(false);
                          }}
                          className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <Calculator className="h-4 w-4 mr-3 text-gray-500" />
                          Calculator
                        </button>
                        <div className="border-t border-gray-100 my-1"></div>
                        <button
                          onClick={handleLogout}
                          className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="h-4 w-4 mr-3" />
                          Logout
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Mobile Menu Button */}
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="md:hidden p-2 rounded-md hover:bg-teal-600 transition-colors"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {showMobileMenu && user && (
          <div className="md:hidden bg-teal-600 border-t border-teal-400">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/dashboard"
                className="block px-3 py-2 text-white hover:bg-teal-700 rounded-md transition-colors"
                onClick={() => setShowMobileMenu(false)}
              >
                📊 Dashboard
              </Link>
              <Link
                to="/create-group"
                className="block px-3 py-2 text-white hover:bg-teal-700 rounded-md transition-colors"
                onClick={() => setShowMobileMenu(false)}
              >
                👥 Create Group
              </Link>
              <Link
                to="/add-expense"
                className="block px-3 py-2 text-white hover:bg-teal-700 rounded-md transition-colors"
                onClick={() => setShowMobileMenu(false)}
              >
                💰 Add Expense
              </Link>
              <button
                onClick={() => {
                  setShowCalculator(true);
                  setShowMobileMenu(false);
                }}
                className="block w-full text-left px-3 py-2 text-white hover:bg-teal-700 rounded-md transition-colors"
              >
                🧮 Calculator
              </button>
              <Link
                to="/transactions"
                className="block px-3 py-2 text-white hover:bg-teal-700 rounded-md transition-colors"
                onClick={() => setShowMobileMenu(false)}
              >
                📜 My Transactions
              </Link>
              <div className="border-t border-teal-500 my-2"></div>
              <button
                onClick={handleLogout}
                className="block w-full text-left px-3 py-2 text-white hover:bg-red-600 rounded-md transition-colors"
              >
                🚪 Logout
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Calculator Modal */}
      <ExpenseCalculator 
        isOpen={showCalculator} 
        onClose={() => setShowCalculator(false)} 
      />
    </>
  );
};

export default Navbar;